import * as React from 'react'
import { Button } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getBpoFailureCountAPI } from 'api/bpo'
import Footer from 'components/Footer'
import Header from 'components/Header'
import loginData from 'utilities/loginData'

/**
 * エラー設定の型定義
 */
interface ErrorConfig {
  title: string
  icon: '📄' | '📷' | '⚖️' | '🆘' | '⏰'
  message: string
  notes?: string[]
  showRetryButton: boolean
  showContactButton: boolean
  contactButtonVariant?: 'primary' | 'secondary'
  footer?: string
}

/**
 * エラータイプの定義
 */
type ErrorType = 'document_error' | 'quality_error' | 'bpo_rejected_first' | 'bpo_rejected_final' | 'timeout'

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [errorConfig, setErrorConfig] = React.useState<ErrorConfig | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)

  /**
   * エラータイプを判定する
   */
  const getErrorType = (action: string | null, errorType: string | null, failureCount: number): ErrorType => {
    if (action === 'timeout') {
      return 'timeout'
    }
    if (action === 'retry') {
      return errorType === 'retry_document' ? 'document_error' : 'quality_error'
    }
    if (failureCount >= 2) return 'bpo_rejected_final'
    return 'bpo_rejected_first'
  }

  /**
   * BPO失敗回数を確認してエラー設定を決定
   */
  const checkFailureCount = React.useCallback(async (fallbackErrorType: ErrorType) => {
    try {
      const response = await getBpoFailureCountAPI()
      
      if (response.status === 'success' && response.response) {
        const { failureCount } = response.response
        const actualErrorType = failureCount >= 2 ? 'bpo_rejected_final' : 'bpo_rejected_first'
        setErrorConfig(getErrorConfig(actualErrorType))
      } else {
        // APIエラーの場合はフォールバック
        setErrorConfig(getErrorConfig(fallbackErrorType))
      }
    } catch (error) {
      console.error('BPO失敗回数確認エラー:', error)
      setErrorConfig(getErrorConfig(fallbackErrorType))
    }
  }, [])

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
      return
    }
    
    // URLパラメータからエラー情報を取得
    const action = searchParams.get('action')
    const errorType = searchParams.get('errorType')
    const failureCount = searchParams.get('failureCount')
    
    // エラータイプを判定
    const currentErrorType = getErrorType(action, errorType, failureCount ? parseInt(failureCount, 10) : 0)
    
    // BPO否認の場合は失敗回数を再確認
    if (currentErrorType === 'bpo_rejected_first' || currentErrorType === 'bpo_rejected_final') {
      checkFailureCount(currentErrorType)
    } else {
      setErrorConfig(getErrorConfig(currentErrorType))
    }
  }, [navigate, searchParams, checkFailureCount])

  /**
   * エラータイプに応じた設定を取得
   */
  const getErrorConfig = (errorType: ErrorType): ErrorConfig => {
    switch (errorType) {
      case 'document_error':
        return {
          title: '再撮影が必要です',
          icon: '📄',
          message: '正しい書類を撮影してください',
          notes: [
            '指定された本人確認書類をご用意ください',
            '有効期限内の書類をお使いください',
            '書類全体が写るように撮影してください'
          ],
          showRetryButton: true,
          showContactButton: false
        }
      
      case 'quality_error':
        return {
          title: '再撮影が必要です',
          icon: '📷',
          message: '画像が不鮮明です。再度撮影してください',
          notes: [
            '明るい場所で撮影してください',
            '書類の文字がはっきり読めることを確認してください',
            '光の反射や影にご注意ください'
          ],
          showRetryButton: true,
          showContactButton: false
        }
      
      case 'bpo_rejected_first':
        return {
          title: '審査が完了できませんでした',
          icon: '⚖️',
          message: '申し訳ございません。審査を完了することができませんでした。',
          showRetryButton: true,
          showContactButton: true,
          contactButtonVariant: 'secondary',
          footer: '※あと1回まで再撮影が可能です'
        }
      
      case 'bpo_rejected_final':
        return {
          title: 'お問い合わせが必要です',
          icon: '🆘',
          message: '恐れ入りますが、カスタマーサポートへお問い合わせください。',
          showRetryButton: false,
          showContactButton: true,
          contactButtonVariant: 'primary',
          footer: '受付時間：平日 9:00-18:00'
        }
      
      case 'timeout':
        return {
          title: '審査がタイムアウトしました',
          icon: '⏰',
          message: '審査開始から3時間が経過したため、タイムアウトしました。お手数ですが、カスタマーサポートへお問い合わせください。',
          showRetryButton: false,
          showContactButton: true,
          contactButtonVariant: 'primary',
          footer: '受付時間：平日 9:00-18:00'
        }
      
      default:
        return {
          title: 'エラーが発生しました',
          icon: '🆘',
          message: 'エラーが発生しました。',
          showRetryButton: false,
          showContactButton: true,
          contactButtonVariant: 'primary'
        }
    }
  }

  /**
   * 再撮影ボタンのクリック処理
   */
  const handleRetryButton = () => {
    setIsLoading(true)
    // 本人確認フローの最初に戻る
    navigate('/registerIdentificationForIndividual')
  }

  /**
   * お問い合わせボタンのクリック処理
   */
  const handleContactButton = () => {
    navigate('/contactUs')
  }

  if (!errorConfig) {
    return (
      <>
        <Header title="読み込み中" />
        <main>
          <section className="mx-4 my-5">
            <div className="text-center">読み込み中...</div>
          </section>
        </main>
        <Footer className="mt-5" />
      </>
    )
  }

  return (
    <>
      <Header title={errorConfig.title} />
      <main>
        <section className="mx-4 my-5">
          <div className="text-center mt-4">
            {/* エラーアイコン */}
            <div className="mb-4">
              <div 
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: '32px'
                }}
              >
                {errorConfig.icon}
              </div>
            </div>
            
            {/* メッセージ */}
            <div className="mb-4">
              <div className="font-15">{errorConfig.message}</div>
            </div>
            
            {/* 注意事項 */}
            {errorConfig.notes && errorConfig.notes.length > 0 && (
              <div className="mb-4 text-start">
                <div className="font-15 fw-bold mb-2">以下の点にご注意ください：</div>
                <div className="note">
                  {errorConfig.notes.map((note, index) => (
                    <div key={index}>・{note}</div>
                  ))}
                </div>
              </div>
            )}
            
            {/* ボタン群 */}
            <div className="mt-4">
              {errorConfig.showRetryButton && (
                <div className="mb-3">
                  <Button 
                    variant="primary" 
                    className="w-100 py-3" 
                    onClick={handleRetryButton}
                    disabled={isLoading}
                  >
                    {isLoading ? '処理中...' : 'もう一度撮影する'}
                  </Button>
                </div>
              )}
              
              {errorConfig.showContactButton && (
                <div className="mb-3">
                  <Button 
                    variant={errorConfig.contactButtonVariant || 'primary'} 
                    className="w-100 py-3" 
                    onClick={handleContactButton}
                  >
                    お問い合わせ
                  </Button>
                </div>
              )}
            </div>
            
            {/* 補足メッセージ */}
            {errorConfig.footer && (
              <div className="note text-muted mt-4">
                <div>{errorConfig.footer}</div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent